import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { AHTPlannedAppointment } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";

interface AHTAppointmentReviewModalProps {
  open: boolean;
  appointments: AHTPlannedAppointment[];
  monthsSupplied: number;
  isDarkMode?: boolean;
  onSave: (updatedAppointments: AHTPlannedAppointment[]) => void;
  onCancel: () => void;
}

type AppointmentAction =
  | { type: "confirm" }
  | { type: "remove" }
  | { type: "amend"; newDate: string };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function AHTAppointmentReviewModal({
  open,
  appointments,
  monthsSupplied,
  isDarkMode,
  onSave,
  onCancel,
}: AHTAppointmentReviewModalProps) {
  const [actions, setActions] = useState<Map<string, AppointmentAction>>(
    new Map(),
  );

  useEffect(() => {
    setActions(new Map());
  }, [appointments]);

  const today = new Date().toISOString().split("T")[0];

  const futureNonTitration = appointments
    .filter((a) => !a.isTitrationAppointment && a.date > today)
    .sort((a, b) => a.date.localeCompare(b.date));

  const affectedCount = Math.max(0, monthsSupplied - 1);
  const affectedIds = new Set(
    futureNonTitration.slice(0, affectedCount).map((a) => a.id),
  );

  const isSixMonthReview = (a: AHTPlannedAppointment) =>
    a.requiresVideoOrF2F === true;

  function getAction(a: AHTPlannedAppointment): AppointmentAction {
    const explicit = actions.get(a.id);
    if (explicit) return explicit;
    if (affectedIds.has(a.id) && isSixMonthReview(a)) {
      return { type: "amend", newDate: a.date };
    }
    return { type: "confirm" };
  }

  const hasPendingAmend =
    affectedIds.size > 0 &&
    Array.from(affectedIds).some((id) => {
      const a = appointments.find((ap) => ap.id === id);
      if (!a) return false;
      const action = getAction(a);
      return (
        action.type === "amend" &&
        (!action.newDate || action.newDate === a.date) &&
        isSixMonthReview(a)
      );
    });

  function handleSave() {
    const updated = appointments
      .filter((a) => {
        const action = getAction(a);
        return action.type !== "remove";
      })
      .map((a) => {
        const action = getAction(a);
        if (action.type === "amend" && action.newDate) {
          return { ...a, date: action.newDate };
        }
        return a;
      });
    onSave(updated);
  }

  const sortedAppointments = [...appointments].sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review appointment schedule</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">
          {monthsSupplied} months supplied — review the {affectedCount}{" "}
          appointment(s) that may be affected.
        </p>

        <div className="max-h-[60vh] overflow-y-auto">
          {sortedAppointments.map((a) => {
            const affected = affectedIds.has(a.id);

            if (!affected) {
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0"
                >
                  <div className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {formatDate(a.date)} — {a.serviceType}
                    </p>
                    {a.requiresVideoOrF2F && (
                      <p className="text-xs text-gray-400">
                        Face-to-face or video required
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">Scheduled</span>
                </div>
              );
            }

            const action = getAction(a);
            const is6Month = isSixMonthReview(a);

            return (
              <div
                key={a.id}
                className="px-4 py-3 border-b border-amber-100 last:border-0 bg-amber-50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900">
                      {formatDate(a.date)} — {a.serviceType}
                    </p>
                    {is6Month ? (
                      <p className="text-xs font-medium text-amber-700 mt-0.5">
                        6-month review — cannot be removed. Bring forward
                        before the patient leaves, or move to when they
                        return.
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-0.5">
                        Patient may not attend — confirm, reschedule, or
                        remove.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-2 ml-5">
                  <Button
                    variant={action.type === "confirm" ? "default" : "outline"}
                    size="sm"
                    onClick={() =>
                      setActions((prev) =>
                        new Map(prev).set(a.id, { type: "confirm" }),
                      )
                    }
                  >
                    Confirm
                  </Button>
                  <Button
                    variant={action.type === "amend" ? "default" : "outline"}
                    size="sm"
                    onClick={() =>
                      setActions((prev) =>
                        new Map(prev).set(a.id, {
                          type: "amend",
                          newDate: a.date,
                        }),
                      )
                    }
                  >
                    Amend date
                  </Button>
                  {!is6Month && (
                    <Button
                      variant={
                        action.type === "remove" ? "destructive" : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setActions((prev) =>
                          new Map(prev).set(a.id, { type: "remove" }),
                        )
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>

                {action.type === "amend" && (
                  <input
                    type="date"
                    className="mt-2 ml-5 text-sm border rounded px-2 py-1 block"
                    value={action.newDate}
                    min={today}
                    onChange={(e) =>
                      setActions((prev) => {
                        const next = new Map(prev);
                        next.set(a.id, {
                          type: "amend",
                          newDate: e.target.value,
                        });
                        return next;
                      })
                    }
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-3 pt-4 border-t mt-2">
          <Button variant="outline" className="flex-1" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            disabled={hasPendingAmend}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
